/* eslint-disable import/no-cycle */
import React, { useCallback, useMemo } from 'react';
import { HStack, IconButton, Box } from '@chakra-ui/core';
import { MdClose } from 'react-icons/md';
import { DragHandleIcon } from '@chakra-ui/icons';
import { QuestionnaireTemplateStore } from '../../../../store/questionnaireTemplateModel';
import colors from '../../../../../../../styles/colors';
import { OptionInput } from './OptionInput';
import { AnimatedWrapper } from '../../../formBuilderComponents/withLayoutAnimation';
import { MultiChoiceOption } from '../../../../questionnaireTemplate.types';

const DRAG_HANDLE_PROPS = {
  'aria-label': 'Drag options element',
  size: 'sm',
  variant: 'ghost',
  cursor: 'grab',
  _active: { cursor: 'grabbing' },
} as const;

const DELETE_BUTTON_PROPS = {
  'aria-label': 'delete option',
  icon: <MdClose />,
  size: 'sm',
  variant: 'ghost',
  colorScheme: 'red',
} as const;

interface OptionItemProps {
  elementId: string;
  option: MultiChoiceOption;
  index: number;
  isRemovable: boolean;
  isActive: boolean;
  isAnyDragging: boolean;
  onDragStart: (e: React.PointerEvent) => void;
}

export const OptionItem = React.memo(
  function OptionItem({ elementId, option, index, isActive, isAnyDragging, isRemovable, onDragStart }: OptionItemProps) {
    const removeOption = QuestionnaireTemplateStore.useStoreActions((a) => a.removeOption);

    const handleRemove = useCallback(() => {
      removeOption({ elementId, optionId: option.id });
    }, [removeOption, elementId, option.id]);

    const handleDragStart = useCallback(
      (e: React.PointerEvent) => {
        e.stopPropagation();
        onDragStart(e);
      },
      [onDragStart],
    );

    const optionLabel = useMemo(() => String.fromCharCode(65 + index), [index]);

    const containerStyles = useMemo(
      () => ({
        spacing: 2,
        bg: 'white',
        borderRadius: 'md',
        border: '1px solid',
        borderColor: isActive ? 'blue.500' : 'transparent',
        boxShadow: isActive ? 'xl' : 'none',
        position: 'relative' as const,
        p: isActive ? 2 : 0,
      }),
      [isActive],
    );

    return (
      <AnimatedWrapper
        motionProps={{
          animate: {
            scale: isActive ? 1.05 : 1,
            zIndex: isActive ? 10 : 1,
            opacity: isAnyDragging && !isActive ? 0.5 : 1,
          },
        }}
      >
        <HStack {...containerStyles} data-testid={`option-item option-${option.id}`}>
          <IconButton
            tabIndex={-1}
            {...DRAG_HANDLE_PROPS}
            icon={<DragHandleIcon fontSize={16} color={colors.gray[300]} />}
            onPointerDown={handleDragStart}
          />

          <Box fontSize="xs" color="gray.500" w="20px">
            {optionLabel}
          </Box>

          <OptionInput elementId={elementId} optionId={option.id} value={option.text} />

          <IconButton tabIndex={-1} {...DELETE_BUTTON_PROPS} onClick={handleRemove} isDisabled={!isRemovable || isAnyDragging} />
        </HStack>
      </AnimatedWrapper>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.option.id === nextProps.option.id &&
      prevProps.option.text === nextProps.option.text &&
      prevProps.index === nextProps.index &&
      prevProps.isActive === nextProps.isActive &&
      prevProps.isAnyDragging === nextProps.isAnyDragging &&
      prevProps.isRemovable === nextProps.isRemovable
    );
  },
);
